import { useMemo, useState } from 'react';
import { Download, FileText, Trash2, UploadCloud } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import { useDb } from '@/lib/store';
import { deleteAttachment, uploadAttachment } from '@/lib/actions';
import { formatBytes, formatDateLong } from '@/lib/format';
import { toast } from '@/components/ui/overlays';
import { uploadError } from '@/lib/upload';
import { IMAGES_ONLY } from '@shared/uploads';

export function AttachmentsSection({ task }: { task: Task }) {
  const all = useDb((db) => db.attachments);
  const items = useMemo(() => Object.values(all).filter((a) => a.taskId === task.id).sort((a, b) => b.createdAt - a.createdAt), [all, task.id]);
  const [over, setOver] = useState(false);
  const upload = async (files: File[]) => {
    for (const f of files) {
      try {
        await uploadAttachment(task.id, f);
      } catch (e) {
        toast(uploadError(e, f));
      }
    }
  };
  return (
    <section
      className={`tm-section tm-attachments ${over ? 'over' : ''}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        void upload([...e.dataTransfer.files]);
      }}
    >
      {items.length > 0 && (
        <>
          <div className="tm-section-head">
            <span className="tm-section-title" style={{ marginLeft: 4 }}>
              Attachments
            </span>
            <span className="faint" style={{ fontSize: 'var(--fs-sm)' }}>
              {items.length}
            </span>
          </div>
          <div className="tm-att-grid">
            {items.map((a) => (
              <div key={a.id} className="tm-att">
                <a className="tm-att-thumb" href={a.url} target="_blank" rel="noreferrer">
                  {a.mime.startsWith('image/') ? <img src={a.url} alt={a.name} loading="lazy" /> : <FileText size={28} className="faint" />}
                </a>
                <div className="tm-att-meta">
                  <div className="truncate tm-att-name" title={a.name}>
                    {a.name}
                  </div>
                  <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                    {formatDateLong(a.createdAt)} · {formatBytes(a.size)}
                  </div>
                </div>
                <div className="tm-att-actions">
                  <a className="icon-btn xs" href={a.url} download={a.name}>
                    <Download size={13} />
                  </a>
                  <button className="icon-btn xs" onClick={() => confirm(`Delete ${a.name}?`) && deleteAttachment(a.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {over && (
        <div className="tm-drop">
          <UploadCloud size={22} /> {IMAGES_ONLY ? 'Drop images to attach' : 'Drop files to attach'}
        </div>
      )}
    </section>
  );
}
